import * as React from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Check, ChevronDown, ChevronRight } from 'lucide-react'

import { cn } from '../../utils/utils'
import { Command, CommandInputLegacy } from './command'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Button } from './button'
import { Sheet, SheetTrigger, SheetContent } from './sheet'
import { useIsMobile } from '../../../shared/store/hooks/useIsMobile'

export type TreeNode =
    | {
          id: string
          name: string
          children?: TreeNode[]
          isLeaf?: never
      }
    | {
          id: string
          name: string
          fullId: string
          isLeaf: true
          children?: never
      }

type FlatNode = TreeNode & {
    level: number
}

type TreeComboboxProps = {
    name: string
    data: TreeNode[]
    value: string | null
    onValueChange?: (value: string) => void
    emptyMessage?: string
    placeholder?: string
    instructionsMessage?: string
    error?: boolean
    portalContainer?: HTMLElement | null
}

export function TreeCombobox({
    data,
    value,
    onValueChange,
    emptyMessage = 'No items found',
    placeholder = 'Type to search...',
    instructionsMessage = '',
    error = false,
    portalContainer,
}: TreeComboboxProps) {
    const isMobile = useIsMobile()
    const [open, setOpen] = React.useState(false)
    const [search, setSearch] = React.useState('')
    const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set())
    const [selectedId, setSelectedId] = React.useState<string | null>(value)
    const [activeIndex, setActiveIndex] = React.useState(0)

    const parentRef = React.useRef<HTMLDivElement>(null)

    // Find the selected node for display
    const selectedNode = React.useMemo(() => {
        const findNode = (nodes: TreeNode[]): TreeNode | null => {
            for (const node of nodes) {
                if (node.isLeaf && node.fullId === selectedId) return node
                if (node.children) {
                    const found = findNode(node.children)
                    if (found) return found
                }
            }
            return null
        }
        return findNode(data)
    }, [data, selectedId])

    const visibleNodes = React.useMemo(() => {
        const flat: FlatNode[] = []
        const searchTerm = search.toLowerCase()

        const matchesSearch = (node: TreeNode): boolean => {
            if (node.name.toLowerCase().includes(searchTerm) || node.id.toLowerCase().includes(searchTerm)) return true
            return node.children?.some(child => matchesSearch(child)) ?? false
        }

        const recurse = (nodes: TreeNode[], level = 0) => {
            for (const node of nodes) {
                if (searchTerm === '' || matchesSearch(node)) {
                    flat.push({ ...node, level })
                    const isExpanded = searchTerm !== '' ? true : expandedIds.has(node.id)
                    if (node.children && isExpanded) recurse(node.children, level + 1)
                }
            }
        }

        recurse(data)
        return flat
    }, [data, expandedIds, search])

    // Determine which is more user friendly - reseting active index on opening seems to work better
    React.useEffect(() => setActiveIndex(0), [open])
    // React.useEffect(() => setActiveIndex(0), [search])

    React.useEffect(() => {
        const leafNodes = visibleNodes.filter(n => n.isLeaf)

        if (leafNodes.length === 1) {
            const leafIndex = visibleNodes.findIndex(n => n.id === leafNodes[0].id)
            if (leafIndex !== -1) {
                setActiveIndex(leafIndex)
            }
        } else if (activeIndex >= visibleNodes.length && visibleNodes.length > 0) {
            // Only reset if current index is out of bounds
            setActiveIndex(0)
        }
    }, [activeIndex, visibleNodes])

    function selectValue(node: TreeNode) {
        if (node.isLeaf && node.fullId) {
            setSelectedId(node.fullId)
            onValueChange && onValueChange(node.fullId)
            setSearch('')
        }
    }

    const rowVirtualizer = useVirtualizer({
        count: visibleNodes.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 40,
        enabled: open,
    })

    // This is a workaround to ensure the virtualizer measures rows after the popover opens,
    // not before, otherwise it might not show any results until the search is used.
    React.useEffect(() => {
        if (!open) return

        const id = setTimeout(() => rowVirtualizer.measure(), 0)
        return () => clearTimeout(id)
    }, [open, rowVirtualizer])

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActiveIndex(prev => Math.min(prev + 1, visibleNodes.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActiveIndex(prev => Math.max(prev - 1, 0))
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault()

            const leafNodes = visibleNodes.filter(n => n.isLeaf)

            // If only one leaf exists, select it (even if user is on parent node)
            if (leafNodes.length === 1) {
                selectValue(leafNodes[0])
                setOpen(false)
                return
            }

            // Otherwise use current active index
            const node = visibleNodes[activeIndex]
            if (!node) return

            if (node.isLeaf) {
                selectValue(node)
                setOpen(false)
            } else {
                toggleNode(node.id)
            }
        } else if (e.key === 'Escape') {
            setOpen(false)
        }
    }

    React.useEffect(() => {
        rowVirtualizer.scrollToIndex(activeIndex, { align: 'auto' })
    }, [activeIndex, rowVirtualizer])

    const toggleNode = (id: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev)
            next.has(id) ? next.delete(id) : next.add(id)
            return next
        })
    }

    const Trigger = (
        <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
                'w-full justify-between  bg-[#f2f2f2] h-auto min-h-[35px] rounded-[10px] shadow-insetSoft text-[#004193] !py-1 whitespace-break-spaces text-left ',
                error && 'border border-red20'
            )}
            title={selectedNode ? selectedNode.name : placeholder}
        >
            <div>{selectedNode ? selectedNode.name : placeholder}</div>
            <ChevronDown
                className={cn(
                    'stroke-grey60 ml-2 size-7 shrink-0 transition-transform duration-200 self-start',
                    open && 'rotate-180',
                    error && 'stroke-red20'
                )}
            />
        </Button>
    )

    const Content = (
        <Command onKeyDown={handleKeyDown} className="rounded-lg">
            <CommandInputLegacy placeholder={instructionsMessage} value={search} onValueChange={setSearch} autoFocus />

            <div ref={parentRef} className="h-[400px] overflow-auto p-0 scrollbar-thin">
                <div
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {visibleNodes.length === 0 && <div className="py-4 text-center text-sm text-gray-500">{emptyMessage}</div>}

                    {rowVirtualizer.getVirtualItems().map(virtualRow => {
                        const node = visibleNodes[virtualRow.index]
                        const isSelected = selectedId === node.id
                        const isActive = activeIndex === virtualRow.index

                        return (
                            <div
                                key={node.id}
                                className={cn(
                                    'absolute left-0 top-0 w-full flex items-center rounded-sm px-2 py-1.5 text-sm transition-colors cursor-pointer select-none',
                                    isActive ? 'bg-blue20 text-blue50' : 'transparent',
                                    isSelected && 'font-semibold'
                                )}
                                style={{
                                    height: `${virtualRow.size}px`,
                                    transform: `translateY(${virtualRow.start}px)`,
                                    paddingLeft: `${node.level * 1 + 0.5}rem`,
                                }}
                                onClick={() => {
                                    setActiveIndex(virtualRow.index)
                                    if (node.isLeaf) {
                                        selectValue(node)
                                        setOpen(false)
                                    } else {
                                        toggleNode(node.id)
                                    }
                                }}
                                title={node.name}
                            >
                                {!node.isLeaf && (
                                    <div className="mr-2 h-4 w-4">
                                        {expandedIds.has(node.id) || search ? (
                                            <ChevronDown className="h-4 w-4 self-start" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4 self-start" />
                                        )}
                                    </div>
                                )}
                                <span className="truncate flex-1">{node.name}</span>
                                {isSelected && <Check className="ml-2 h-4 w-4" />}
                            </div>
                        )
                    })}
                </div>
            </div>
        </Command>
    )

    return isMobile ? (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>{Trigger}</SheetTrigger>

            <SheetContent
                side="bottom"
                className="p-0 bottom-0 z-[9999]"
                container={portalContainer}
                onPointerDownOutside={e => e.preventDefault()}
                onInteractOutside={e => e.preventDefault()}
            >
                {Content}
            </SheetContent>
        </Sheet>
    ) : (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{Trigger}</PopoverTrigger>

            <PopoverContent className="p-0 bg-white" align="start" style={{ width: 'var(--radix-popover-trigger-width)', zIndex: 1000 }}>
                {Content}
            </PopoverContent>
        </Popover>
    )
}
